/**
 * The cashflow & Age Pension plan page (cashflow rebuild, 21 Oct 2026; the agreed mock-up's j1-hub and
 * j2-hub, the village's RvHub).
 *
 * Where the module opens once a plan is chosen — from Home, the client menu or Support at home's door —
 * with everything as it was left (review, 18 Oct 2026: "a saved plan opens a blank page; the support cost,
 * the option and the years are all lost"). Every figure is the chosen card's own, as it was chosen.
 *
 * BUILT FROM THE SHARED CHOSEN-PLAN PIECES (owner, 22 Oct 2026: "Yes i want this all consistent"): the card,
 * the document and next step, "Model something else" and the ways on are aged care's (ChosenPlanPage.tsx),
 * not this page's own. The support cost it was priced on stays above the card, with "Change it".
 */
import React from 'react';
import { ArrowLeftRight, BookOpen, Hammer, Wallet } from 'lucide-react';
import { householdVoice } from '../../utils/householdWords.ts';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { PlanOutOfDateBanner } from '../../components/strategy/PlanOutOfDateBanner.tsx';
import { ChosenPlanCard, DocumentAndNextStep, ModelSomethingElse, PlanPageLinks, type PlanFigure, type ModelOption } from '../../components/strategy/ChosenPlanPage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { CpPlanCardRecord } from './cpChosenPlan.ts';
import { cpAmountsOf, cpTiedUpOf, type CpCandidateLike } from './cpPlanWords.ts';
import { money, signed, cashWords, link } from './cpUi.tsx';
import { runwayCardLine } from '../agedCare/moneyStory.tsx';

export interface CpHubProps {
    factFind: DraftFactFind;
    card: CpPlanCardRecord;
    /** The plan as it was run, when the record keeps it — what it puts out of reach and its key fact. */
    candidate?: CpCandidateLike | null;
    chosenOn?: string | null;
    soaPreparedAt?: string | null;
    onChangeSupport: () => void;
    onExplain: () => void;
    onRisks: () => void;
    onAdvice: () => void;
    onAllPlans: () => void;
    onBuild: () => void;
    /** "Build a plan yourself": a blank plan (25 Oct 2026). */
    onBuildNew?: () => void;
    /** Why the chosen plan's figures are out of date (chosenPlanRefresh.ts), or null (25 Oct 2026). */
    outOfDate?: string | null;
    onQuote: () => void;
    onAssumptions: () => void;
    onHome: () => void;
    /** "A new model from scratch": step 1 again, the questions asked afresh (owner, 22 Oct 2026). */
    onStartFresh?: () => void;
    /** "Change the support first": only when the page was opened from Support at home. */
    onChangeSupportFirst?: (() => void) | null;
    /** "Back to Support at home", when that was the door. */
    back?: { label: string; onClick: () => void } | null;
}

const dateWords = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

/** The plan's key fact, as its card said it when chosen ("Worth knowing"). */
function keyFactOf(card: CpPlanCardRecord, candidate: CpCandidateLike | null | undefined): string | null {
    const m = candidate ? cpAmountsOf(candidate) : null;
    const annuity = m?.annuity || card.annuity?.amount || 0;
    const guaranteed = m ? m.annuityType === 'guaranteed' : card.annuity?.type === 'guaranteed';
    const residual = m ? m.residual : (card.annuity?.residualPercent || 0) / 100;
    if (annuity > 0 && !guaranteed && residual <= 0) {
        return 'The guaranteed income is income only. It pays the most for life, and nothing from it goes back to the family, even after an early death.';
    }
    if (annuity > 0) {
        return `The ${money(annuity)} is committed for life.${guaranteed ? ' It pays the least income of the three types, in return for the full amount back to the family.' : ' The money back to the family falls over time, to nothing later in life.'}`;
    }
    const pensionSame = !card.baseline || Math.abs(card.plan.pension - card.baseline.pension) < 1;
    if (m && (m.superTotal > 0 || m.investmentBond > 0) && pensionSame) {
        return 'This is about tax, not the Age Pension: Centrelink counts the money the same way it counts the savings it replaces.';
    }
    return null;
}

const CpHub = ({ factFind, card, candidate = null, chosenOn, soaPreparedAt, onChangeSupport, onExplain, onRisks, onAdvice, onAllPlans, onBuild, onBuildNew, outOfDate = null, onQuote, onAssumptions, onHome, onStartFresh, onChangeSupportFirst, back }: CpHubProps) => {
    const v = householdVoice(factFind);
    const base = card.baseline;
    const delta = base ? card.plan.legacy - base.legacy : null;
    const prepared = dateWords(soaPreparedAt);
    const pDelta = base ? card.plan.pension - base.pension : 0;
    const tied = candidate ? cpTiedUpOf(candidate) : 0;
    const keyFact = keyFactOf(card, candidate);

    /* The strip of figures (owner, 22 Oct 2026): the Age Pension, each year's money, and what it puts out of reach. */
    const figures: PlanFigure[] = [
        {
            label: 'Age Pension',
            value: money(card.plan.pension),
            unit: 'a year',
            note: base ? (Math.abs(pDelta) >= 1 ? `${signed(pDelta)} a year vs changing nothing` : 'the same as changing nothing') : undefined,
            testId: 'cp-hub-pension',
        },
        {
            // "Each year, short by" when a year runs short, as aged care and support at home say it (22 Oct 2026).
            label: card.plan.cashflow < 0 ? 'Each year, short by' : 'Left over each year',
            value: money(Math.abs(card.plan.cashflow)),
            note: base ? `changing nothing: ${cashWords(base.cashflow)}` : undefined,
            tone: card.plan.cashflow > 0 ? 'good' : 'plain',
            testId: 'cp-hub-each-year',
        },
    ];
    if (tied > 0) figures.push({ label: 'Put out of reach', value: money(tied), note: 'gifted, kept for the funeral or committed for life', testId: 'cp-hub-tied-up' });

    const options: ModelOption[] = [];
    if (onStartFresh) {
        options.push({ title: 'A new model from scratch', body: 'Starts again at step 1 with nothing carried over, and asks the three questions again so you can change any of them.', action: 'Start a new model from scratch', onClick: onStartFresh, testId: 'cp-hub-start-fresh' });
    }
    if (onChangeSupportFirst) {
        options.push({ title: 'Change the support first', body: 'Keeps the answers to the three questions. Starts at step 1 so you can change the support paid for, then tests the plans again.', action: 'Change the support', onClick: onChangeSupportFirst, testId: 'cp-hub-change-support-first' });
    }

    return (
        <div className="space-y-5" data-testid="cp-hub">
            <div>
                <p className="text-[13px] text-slate-500">
                    <button type="button" onClick={onHome} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button> › Cashflow &amp; Age Pension
                    {back && <> · <button type="button" onClick={back.onClick} className="underline decoration-dotted underline-offset-2 hover:text-slate-800" data-testid="cp-hub-back">{back.label}</button></>}
                </p>
                <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="cp-hub-title">{v.you ? 'Your money plan' : `${v.Possessive} money plan`}</h1>
                <p className="mt-1 text-[15px] text-slate-600">
                    {card.support.annual > 0 ? <>With {money(card.support.annual)} a year of {card.support.label.toLowerCase()}.</> : <>With no support paid for.</>}{' '}
                    <button type="button" onClick={onChangeSupport} className={link} data-testid="cp-hub-change-support">Change it</button>
                </p>
            </div>

            {/* A chosen plan whose figures are out of date says so, with a way to choose again (25 Oct 2026). */}
            <PlanOutOfDateBanner reason={outOfDate} onChooseAgain={onAllPlans} testId="cp-hub-out-of-date" />
            <ChosenPlanCard
                testIdPrefix="cp-hub"
                chosenOn={dateWords(chosenOn)}
                builtByYou={card.origin === 'manual'}
                title={card.words.title}
                sub={card.words.sub}
                estate={card.plan.legacy}
                years={card.horizonYears}
                estateDelta={delta}
                baseWord="changing nothing"
                figures={figures}
                onExplain={onExplain}
                onRisks={onRisks}
                onAllPlans={onAllPlans}
            >
                {/* When the money runs out, from the rows saved with the card (23 Oct 2026); an older card has none. */}
                {(() => {
                    const line = card.rows && card.rows.length ? runwayCardLine({ rows: card.rows as never, factFind, horizonYears: card.horizonYears }) : null;
                    return line ? <p className="mb-4 text-[14px] leading-relaxed text-slate-700" data-testid="cp-hub-runline"><strong className="text-slate-900">When the money runs out:</strong> {line}</p> : null;
                })()}
                {card.family && card.family.rows.length > 0 && (
                    <AnnuityFamilyPanel
                        factFind={factFind}
                        personName={card.family.personName}
                        amount={card.family.amount}
                        kind={card.family.kind}
                        incomeAYear={card.family.incomeAYear}
                        rows={null}
                        frozen={card.family.rows}
                        compareNote={card.family.compareNote}
                        horizonYears={card.horizonYears}
                        testId="cp-hub-family-panel"
                    />
                )}
                {keyFact && (
                    <p className={`${card.family && card.family.rows.length > 0 ? 'mt-4 ' : ''}text-[13px] leading-relaxed text-slate-700`} data-testid="cp-hub-key-fact">
                        <strong className="text-slate-900">Worth knowing:</strong> {keyFact}
                    </p>
                )}
            </ChosenPlanCard>

            <div data-testid="cp-hub-next">
                <DocumentAndNextStep
                    documentState={prepared ? `prepared ${prepared}` : 'not prepared yet'}
                    documentLine="The written record of this plan, what it changes and why."
                    documentAction={<button type="button" onClick={onAdvice} className={link} data-testid="cp-hub-advice">Get the advice document</button>}
                    nextTitle={card.annuity ? 'get a quote from the provider' : 'tell Services Australia'}
                    nextLine={card.annuity
                        ? <>The income shown for the {money(card.annuity.amount)} guaranteed income is an estimate from the published rate; a quote may differ. <button type="button" onClick={onQuote} className={link} data-testid="cp-hub-quote">Enter the quote</button>, or see the</>
                        : 'Each change moves what Centrelink counts. Report it within 14 days so the Age Pension is worked out on the new figures. See the'}
                    nextAction={<button type="button" onClick={onHome} className={link} data-testid="cp-hub-home-plan">Full action plan on Home</button>}
                />
            </div>

            {options.length > 0 && <ModelSomethingElse options={options} testId="cp-hub-model-something-else" />}

            <PlanPageLinks
                links={[
                    { label: 'All the plans we tested', icon: <ArrowLeftRight size={14} />, onClick: onAllPlans, testId: 'cp-hub-link-all-plans' },
                    // Two doors (owner, 25 Oct 2026): change this plan, or start a blank one.
                    { label: card.origin === 'manual' ? 'Change the plan you built' : 'Customise this plan', icon: <Hammer size={14} />, onClick: onBuild, testId: 'cp-hub-build' },
                    ...(onBuildNew ? [{ label: 'Build a plan yourself', icon: <Hammer size={14} />, onClick: onBuildNew, testId: 'cp-hub-build-new' }] : []),
                    { label: `${v.Possessive} money today`, icon: <Wallet size={14} />, onClick: onChangeSupport, testId: 'cp-hub-money-today' },
                    { label: 'Rates and assumptions', icon: <BookOpen size={14} />, onClick: onAssumptions, testId: 'cp-hub-assumptions' },
                ]}
            />
        </div>
    );
};

export default CpHub;
